import { HERO_RANK_BANDS } from "@/components/shared/HeroRankColorKey";
import { cn } from "@/lib/utils";

/**
 * Background-only tone classes for the three ranked heat bands, handed to the Hero Rank column
 * header's `HeroRankColorKey` (AUT-457) so the key and the pill can never show different colours.
 * The bands themselves come from `HERO_RANK_BANDS`: these literals used to be a third, independent
 * copy of 15 and 50, which is how rank 50 ended up amber here and red on the Keywords tab.
 */
export const HERO_RANK_TONE_CLASSES = {
  top: "bg-[hsl(var(--heat-rank-10))]",
  mid: "bg-[hsl(var(--heat-rank-50))]",
  terminal: "bg-[hsl(15_90%_55%)] dark:bg-[hsl(15_82%_50%)]",
} as const;

/**
 * The Keywords tab bank picker's Hero Rank cell (WEB-006,
 * `prototype-extracted.md § 4.7`, `§ 6`). The design's `bg-heat-2` / `bg-heat-4` /
 * `bg-heat-6` + `text-heat-on-dark` / `text-heat-on-light` classes emit no CSS in
 * `frontend-new` -- resolved to the values behind them instead of ported as class
 * names. `--heat-rank-10` / `--heat-rank-50` already carry the design's `--heat-2` /
 * `--heat-4` values (`tokens.css:121,123,242,244`); `--heat-6` has no equivalent in
 * our six-stop scale, so its light/dark HSL is written literally here rather than
 * adding a variable for a single caller.
 */
export function HeroRankPill({ rank }: { rank: number | null }) {
  if (rank === null) {
    return (
      <span className="relative flex items-center justify-center rounded-sm py-2 text-center text-xs tabular-nums bg-muted text-muted-foreground">
        NA
      </span>
    );
  }

  const toneClassName =
    rank <= HERO_RANK_BANDS.greenMax
      ? cn(HERO_RANK_TONE_CLASSES.top, "text-[hsl(var(--heat-on-dark))]")
      : rank <= HERO_RANK_BANDS.yellowMax
        ? cn(HERO_RANK_TONE_CLASSES.mid, "text-[hsl(var(--heat-on-light))]")
        : cn(HERO_RANK_TONE_CLASSES.terminal, "text-[hsl(var(--heat-on-dark))]");

  return (
    <span
      className={cn(
        "relative flex items-center justify-center rounded-sm py-2 text-center text-xs tabular-nums",
        toneClassName,
      )}
    >
      {rank}
    </span>
  );
}
